import { useId, type FormEvent } from "react";
import { RefreshCw } from "lucide-react";
import type { ExchangeRate, ExchangeRateInput } from "@/api/books";
import { CURRENCY_CODE, plainRate, quoteSource, useQuoteLookup } from "@/api/fx";
import { RequestError } from "@/components/RequestError";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { todayIso } from "@/lib/dates";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type RateFormProps = {
  initial: ExchangeRate | null;
  /** A new rate for this currency and day, e.g. from a "waiting for a rate" link. */
  defaults?: { currency: string; on: string };
  onSubmit: (input: ExchangeRateInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

/** Rupees per unit of a currency on a day. The latest rate on or before a date is used. */
export function RateForm({ initial, defaults, onSubmit, onCancel, onReload }: RateFormProps) {
  const id = useId();
  const { values, set } = useFormValues({
    on: initial?.on ?? defaults?.on ?? todayIso(),
    currency: initial?.currency ?? defaults?.currency ?? "USD",
    rate_inr: initial?.rate_inr ?? "",
    source: initial?.source ?? "",
  });
  const { error, errors, saving, run } = useSubmitState();
  const quote = useQuoteLookup();
  const currency = values.currency.trim().toUpperCase();
  const canFetch = CURRENCY_CODE.test(currency) && currency !== "INR" && Boolean(values.on);

  async function fetchRate(): Promise<void> {
    const found = await quote.lookup(values.on, [currency, "INR"]);
    if (!found?.[0]) return;
    set("rate_inr", plainRate(found[0].rate));
    set("source", quoteSource(found[0]));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        on: values.on,
        currency,
        rate_inr: values.rate_inr.trim(),
        source: values.source.trim(),
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-4">
        <TextField
          id={`${id}-on`}
          label="Date"
          type="date"
          value={values.on}
          onChange={(v) => set("on", v)}
          error={errors.on}
        />
        <TextField
          id={`${id}-currency`}
          label="Currency"
          value={values.currency}
          onChange={(v) => set("currency", v.toUpperCase())}
          error={errors.currency}
          maxLength={3}
        />
        <TextField
          id={`${id}-rate`}
          label="Rupees per unit"
          inputMode="decimal"
          value={values.rate_inr}
          onChange={(v) => set("rate_inr", v)}
          error={errors.rate_inr}
          hint="The RBI reference rate or your bank's rate on that day"
        />
        <TextField
          id={`${id}-source`}
          label="Source"
          value={values.source}
          onChange={(v) => set("source", v)}
          error={errors.source}
          hint="e.g. RBI reference, SBI TT buying"
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => void fetchRate()}
            disabled={!canFetch || quote.busy}
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            {quote.busy ? "Fetching…" : "Fetch rate"}
          </Button>
          <span className="text-xs text-muted-foreground">
            Fills in the market rate published for that day. Check it against your bank before
            saving; nothing is saved until you add the rate.
          </span>
        </div>
        <RequestError error={quote.error} />
      </div>
      <FormActions saving={saving} submitLabel={initial ? "Save rate" : "Add rate"} onCancel={onCancel} />
    </form>
  );
}
